import { useState, useEffect, useMemo } from "react";
import { getNamespaceTranslations } from "../lib/questionnaire-translations";
import {
  getRecommendedDeliveryDays,
  getRecommendedSellingPlan,
  getVariantRecommendedDeliveryDays,
  isMensProduct,
  isRecommendedPlanForDeliveryDays,
  parseFrequencyTiersMetafield,
} from "../lib/subscription-frequency";
import { Button } from "./ui/button";
import { Header } from "./Header";
import { ProgressBar } from "./ui/ProgressBar";
import type { ProgressSectionId } from "../lib/progress-calculator";

// States that require $10 surcharge on consultation fee
const SURCHARGE_STATES = ['Oregon', 'Hawaii', 'Rhode Island', 'Maine', 'Alaska', 'Delaware', 'Kansas', 'Mississippi', 'West Virginia', 'Missouri', 'Arkansas'];
const SURCHARGE_AMOUNT = 10;

interface SubscriptionSelectionProps {
  onContinue: (selectedFrequency: string, sellingPlanId: string) => void;
  onEditDose: () => void;
  onEditQuantity: () => void;
  onBack: () => void;
  onClose?: () => void;
  language?: "en" | "es";
  logoUrl?: string;
  productData?: any;
  selectedDose: string;
  selectedQuantity: string;
  consultationFee?: number;
  consultationProductId?: string;
  consultationProductHandle?: string;
  consultationProductTitle?: string;
  consultationPageTitle?: string;
  currentStep?: number;
  totalSteps?: number;
  sectionIndex?: number;
  totalSections?: number;
  sectionId?: ProgressSectionId;
  chosenFrequency: string;
  chosenSellingPlanId: string;
  updateCookie: (progress: string, data?: object) => void;
  shippingFee?: number;
  shippingFeeEnabled?: boolean;
  selectedState?: string;
  productHandle?: string;
}

export function SubscriptionSelection({
  onContinue,
  onEditDose,
  onEditQuantity,
  onBack,
  onClose,
  language = "en",
  logoUrl,
  productData,
  selectedDose,
  selectedQuantity,
  consultationFee,
  consultationProductId: _consultationProductId,
  consultationProductHandle: _consultationProductHandle,
  consultationProductTitle,
  consultationPageTitle: _consultationPageTitle,
  currentStep,
  totalSteps,
  sectionIndex,
  totalSections,
  sectionId,
  chosenFrequency,
  chosenSellingPlanId,
  updateCookie,
  shippingFee = 13,
  shippingFeeEnabled = true,
  selectedState,
  productHandle = "",
}: SubscriptionSelectionProps) {
  const t = getNamespaceTranslations(language, "productSelection");
  const [selectedFrequency, setSelectedFrequency] = useState<string>(chosenFrequency);
  const [selectedSellingPlanId, setSelectedSellingPlanId] = useState<string>(chosenSellingPlanId);
  const [selectionError, setSelectionError] = useState("");
  const subscriptionT = t?.subscriptionSelection;

  const handleContinue = () => {
    if (!selectedFrequency || !selectedSellingPlanId) {
      setSelectionError(subscriptionT?.selectionRequired || "This field is required");
      document.querySelector<HTMLElement>('[data-testid^="subscription_radio"]')?.focus();
      return;
    }
    setSelectionError("");
    onContinue(selectedFrequency, selectedSellingPlanId);
  };

  // Get all selling plans from ALL groups (not just the first one)
  const allGroups = productData?.sellingPlanGroups?.edges || [];
  const sellingPlans = allGroups.flatMap((groupEdge: any) =>
    groupEdge.node.sellingPlans?.edges || []
  );

  // Find the selected variant based on dose and quantity
  const selectedVariant = productData?.variants?.edges?.find((edge: any) => {
    const options = edge.node.selectedOptions;

    // Check if this variant matches both selected dose and quantity
    const doseMatch = options?.some((opt: any) => opt.value === selectedDose);
    const quantityMatch = options?.some((opt: any) => opt.value === selectedQuantity);

    return doseMatch && quantityMatch;
  })?.node;

  const productFrequencyTiers = useMemo(
    () =>
      parseFrequencyTiersMetafield(
        productData?.metafields?.find(
          (mf: any) => mf?.key === "subscription_frequency_tiers"
        )?.value
      ),
    [productData?.metafields]
  );

  const variantRecommendedDeliveryDays = getVariantRecommendedDeliveryDays(selectedVariant);

  const frequencyConfig = useMemo(
    () => ({
      variantDeliveryDays: variantRecommendedDeliveryDays,
      productTiers: productFrequencyTiers,
    }),
    [variantRecommendedDeliveryDays, productFrequencyTiers]
  );

  const isMensFlow = isMensProduct(productHandle);
  const recommendedDeliveryDays = useMemo(
    () =>
      isMensFlow
        ? getRecommendedDeliveryDays(selectedQuantity, frequencyConfig)
        : null,
    [isMensFlow, selectedQuantity, frequencyConfig]
  );

  const recommendedPlan = useMemo(
    () =>
      isMensFlow
        ? getRecommendedSellingPlan(sellingPlans, selectedQuantity, frequencyConfig)
        : null,
    [isMensFlow, sellingPlans, selectedQuantity, frequencyConfig]
  );

  // Auto-select recommended frequency for men's flow, or first plan as fallback
  useEffect(() => {
    if (!selectedFrequency && !selectedSellingPlanId && sellingPlans.length > 0) {
      const planToSelect = recommendedPlan ?? sellingPlans[0].node;
      setSelectedFrequency(planToSelect.name);
      setSelectedSellingPlanId(planToSelect.id);
      updateCookie('subscription-selection', {
        subscriptionSelection: {
          frequency: planToSelect.name,
          sellingPlanId: planToSelect.id
        }
      });
    }
  }, [sellingPlans, selectedFrequency, selectedSellingPlanId, recommendedPlan, updateCookie]);

  // Get product title
  const productTitle = productData?.title || '';

  // Get product image
  const productImage = productData?.images?.edges?.[0]?.node?.src || '';

  // Get variant price
  const variantPrice = selectedVariant?.price?.amount
    ? parseFloat(selectedVariant.price.amount)
    : 0;

  // Calculate pricing based on selected selling plan
  const selectedPlan = sellingPlans.find((edge: any) => edge.node.id === selectedSellingPlanId)?.node;

  // Get discount percentage from selling plan (if any)
  // Recharge typically stores this in the selling plan pricing policies
  const priceAdjustments = selectedPlan?.priceAdjustments || [];
  const percentageDiscount = priceAdjustments.length > 0
    ? priceAdjustments[0]?.adjustmentValue?.adjustmentPercentage || 0
    : 0;

  // Calculate discounted price
  const discountAmount = variantPrice * (percentageDiscount / 100);
  const discountedPrice = variantPrice - discountAmount;

  // Add shipping fee to prices (only if enabled)
  const discountedPriceWithShipping = shippingFeeEnabled ? discountedPrice + shippingFee : discountedPrice;

  // Check if state surcharge applies
  const hasSurcharge = selectedState ? SURCHARGE_STATES.includes(selectedState) : false;
  const consultationFeeWithSurcharge = (consultationFee || 0) + (hasSurcharge ? SURCHARGE_AMOUNT : 0);

  // Calculate totals (only include consultation fee if product is configured)
  const dueToday = discountedPriceWithShipping + consultationFeeWithSurcharge;
  const recurringPrice = discountedPriceWithShipping;

  // Calculate per-dose price
  // Extract number from quantity string (e.g., "20 doses" -> 20)
  const quantityMatch = selectedQuantity.match(/\d+/);
  const quantityNumber = quantityMatch ? parseInt(quantityMatch[0]) : 1;
  const perDosePrice = recurringPrice / quantityNumber;

  // Calculate savings
  const savingsAmount = variantPrice - discountedPrice;
  const savingsPercentage = variantPrice > 0
    ? Math.round((savingsAmount / variantPrice) * 100)
    : 0;

  // Get variant quantity badge from metafield
  const variantQuantityBadge = selectedVariant?.metafields?.find((mf: any) => mf?.key === 'variant_quantity_badge')?.value || null;

  // Get dose and quantity option counts to determine if edit buttons should be shown
  const doseOption = productData?.options?.find((opt: any) =>
    opt.name.toLowerCase().includes('dose') ||
    opt.name.toLowerCase().includes('dosis') ||
    opt.name.toLowerCase().includes('dosage') ||
    opt.name.toLowerCase().includes('strength')
  );
  const quantityOption = productData?.options?.find((opt: any) =>
    opt.name.toLowerCase().includes('quantity') ||
    opt.name.toLowerCase().includes('size') ||
    opt.name.toLowerCase().includes('count') ||
    opt.name.toLowerCase().includes('tablets') ||
    opt.name.toLowerCase().includes('cantidad')
  );

  const doseOptionsCount = doseOption?.values?.length || 0;
  const quantityOptionsCount = quantityOption?.values?.length || 0;
  const showDoseEdit = doseOptionsCount > 1;
  const showQuantityEdit = quantityOptionsCount > 1;

  if (!subscriptionT) {
    return null;
  }

  if (sellingPlans.length === 0) {
    console.warn('[SubscriptionSelection] No selling plans found');
    return null;
  }

  if (!selectedVariant) {
    console.warn('[SubscriptionSelection] Could not find matching variant for selected options');
    return null;
  }

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Header logoUrl={logoUrl} onBack={onBack} onClose={onClose} />

      <div className="flex-1 flex flex-col bg-gradient-to-b from-[#FAFAFA] to-[#E8F0F3]">
        {/* Progress Bar */}
        <div className="px-4 sm:px-6 pt-[30px]">
          <div className="max-w-[640px] mx-auto">
            {currentStep !== undefined &&
              totalSteps !== undefined &&
              sectionIndex !== undefined &&
              totalSections !== undefined &&
              sectionId !== undefined && (
              <ProgressBar
                currentStep={currentStep}
                totalSteps={totalSteps}
                sectionIndex={sectionIndex}
                totalSections={totalSections}
                sectionId={sectionId}
                language={language}
              />
            )}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 px-4 sm:px-6 pt-8 pb-[30]">
          <div className="max-w-[640px] mx-auto">
            {/* Product Summary Card */}
            <div className="bg-white mb-[30px] overflow-hidden border border-[#E0E0E0] rounded-[6px]">
              <div className="flex">
                {/* Product Image */}
                {productImage && (
                  <div className="flex-shrink-0">
                    <img
                      src={productImage}
                      alt={`${productData?.title || 'Product'} - ${selectedDose}, ${selectedQuantity}`}
                      className="w-[111px] sm:w-[213px] h-full object-cover rounded-tl-[6px] rounded-bl-[6px]"
                    />
                  </div>
                )}

                {/* Product Details */}
                <div className="flex-1 p-3 sm:p-4">
                  {/* Product Title */}
                  <h2 className="text-black mb-4 font-normal text-[30px] leading-[36px]" style={{ fontFamily: 'Gelica' }}>
                    {productData?.title}
                  </h2>

                  {/* Dosage and Quantity Container */}
                  <div className="flex flex-col gap-3">
                    {/* Dosage */}
                    <div className="bg-[#F7F1F1] p-2 rounded-[6px]">
                      <div className="text-black mb-1 font-normal text-xs leading-[18px]" style={{ fontFamily: 'TT Commons Pro' }}>
                        {subscriptionT.dosage}
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="text-black font-medium text-sm leading-5" style={{ fontFamily: 'TT Commons Pro' }}>
                          {productTitle} {selectedDose}
                        </div>
                        {showDoseEdit && (
                          <button
                            type="button"
                            onClick={onEditDose}
                            aria-label={`${subscriptionT.edit} ${subscriptionT.dosage}`}
                            data-testid="subscription-edit_dose"
                            className="flex items-center gap-1 text-black bg-white font-medium text-xs leading-5 text-right px-2 rounded-[20px]"
                            style={{ fontFamily: 'TT Commons Pro' }}
                          >
                            <svg width="11" height="11" viewBox="0 0 11 11" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
                              <path d="M8.04688 4.62891L7.59766 5.07812L3.71094 8.96484C3.51562 9.17969 3.26172 9.31641 2.98828 9.41406L0.625 10.0977C0.46875 10.1367 0.292969 10.0977 0.175781 9.98047C0.0390625 9.86328 0 9.6875 0.0585938 9.51172L0.742188 7.16797C0.820312 6.89453 0.976562 6.64062 1.17188 6.42578L5.05859 2.53906L5.50781 2.10938L5.72266 1.875L6.38672 2.53906L7.59766 3.75L8.26172 4.41406L8.04688 4.62891ZM3.16406 7.92969H2.53906C2.36328 7.92969 2.22656 7.79297 2.22656 7.61719V6.99219L1.77734 7.16797C1.71875 7.24609 1.66016 7.32422 1.64062 7.42188L1.19141 8.94531L2.71484 8.49609C2.8125 8.47656 2.89062 8.4375 2.96875 8.37891L3.16406 7.92969ZM7.10938 0.488281C7.59766 0 8.39844 0 8.88672 0.488281L9.64844 1.26953C10.1367 1.75781 10.1367 2.53906 9.64844 3.02734L9.375 3.32031L8.92578 3.75L8.71094 3.96484L8.04688 3.32031L6.83594 2.08984L6.17188 1.44531L6.38672 1.21094L6.83594 0.78125L7.10938 0.488281ZM6.19141 3.76953C6.30859 3.65234 6.30859 3.45703 6.19141 3.33984C6.07422 3.20312 5.85938 3.20312 5.74219 3.33984L2.92969 6.15234C2.8125 6.26953 2.8125 6.46484 2.92969 6.58203C3.04688 6.69922 3.26172 6.69922 3.37891 6.58203L6.19141 3.76953Z" fill="black"/>
                            </svg>
                            {subscriptionT.edit}
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Quantity */}
                    <div className="bg-[#F7F1F1] p-2 rounded-[6px]">
                      <div className="text-black mb-1 font-normal text-xs leading-[18px]" style={{ fontFamily: 'TT Commons Pro' }}>
                        {subscriptionT.quantity}
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="text-black font-medium text-sm leading-5" style={{ fontFamily: 'TT Commons Pro' }}>
                          {selectedQuantity}
                        </div>
                        {showQuantityEdit && (
                          <button
                            type="button"
                            onClick={onEditQuantity}
                            aria-label={`${subscriptionT.edit} ${subscriptionT.quantity}`}
                            data-testid="subscription-edit_quantity"
                            className="flex items-center gap-1 text-black bg-white font-medium text-xs leading-5 text-right px-2 rounded-[20px]"
                            style={{ fontFamily: 'TT Commons Pro' }}
                          >
                            <svg width="11" height="11" viewBox="0 0 11 11" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
                              <path d="M8.04688 4.62891L7.59766 5.07812L3.71094 8.96484C3.51562 9.17969 3.26172 9.31641 2.98828 9.41406L0.625 10.0977C0.46875 10.1367 0.292969 10.0977 0.175781 9.98047C0.0390625 9.86328 0 9.6875 0.0585938 9.51172L0.742188 7.16797C0.820312 6.89453 0.976562 6.64062 1.17188 6.42578L5.05859 2.53906L5.50781 2.10938L5.72266 1.875L6.38672 2.53906L7.59766 3.75L8.26172 4.41406L8.04688 4.62891ZM3.16406 7.92969H2.53906C2.36328 7.92969 2.22656 7.79297 2.22656 7.61719V6.99219L1.77734 7.16797C1.71875 7.24609 1.66016 7.32422 1.64062 7.42188L1.19141 8.94531L2.71484 8.49609C2.8125 8.47656 2.89062 8.4375 2.96875 8.37891L3.16406 7.92969ZM7.10938 0.488281C7.59766 0 8.39844 0 8.88672 0.488281L9.64844 1.26953C10.1367 1.75781 10.1367 2.53906 9.64844 3.02734L9.375 3.32031L8.92578 3.75L8.71094 3.96484L8.04688 3.32031L6.83594 2.08984L6.17188 1.44531L6.38672 1.21094L6.83594 0.78125L7.10938 0.488281ZM6.19141 3.76953C6.30859 3.65234 6.30859 3.45703 6.19141 3.33984C6.07422 3.20312 5.85938 3.20312 5.74219 3.33984L2.92969 6.15234C2.8125 6.26953 2.8125 6.46484 2.92969 6.58203C3.04688 6.69922 3.26172 6.69922 3.37891 6.58203L6.19141 3.76953Z" fill="black"/>
                            </svg>
                            {subscriptionT.edit}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Shipping Frequency Section */}
            <div className="mb-[30px]">
              <h3
                id="frequency-title"
                className="text-black mb-2 font-semibold text-base leading-[22px]"
                style={{ fontFamily: 'TT Commons Pro' }}
              >
                {subscriptionT.frequencyTitle}
              </h3>
              <p className="text-gray-600 mb-4 font-normal text-sm leading-[21px]" style={{ fontFamily: 'TT Commons Pro' }}>
                {subscriptionT.frequencySubtitle}
              </p>

              {/* Frequency Options */}
              <div
                role="radiogroup"
                aria-labelledby="frequency-title"
                className="flex flex-col gap-3"
              >
                {sellingPlans.map((planEdge: any) => {
                  const plan = planEdge.node;
                  const isSelected = selectedSellingPlanId === plan.id;
                  const isRecommended =
                    isMensFlow &&
                    isRecommendedPlanForDeliveryDays(plan, recommendedDeliveryDays);

                  return (
                    <button
                      key={plan.id}
                      type="button"
                      role="radio"
                      aria-checked={isSelected}
                      onClick={() => {
                        setSelectedSellingPlanId(plan.id);
                        setSelectedFrequency(plan.name);
                        if (selectionError) setSelectionError("");
                        // Save selection to cookie immediately
                        updateCookie('subscription-selection', {
                          subscriptionSelection: {
                            frequency: plan.name,
                            sellingPlanId: plan.id
                          }
                        });
                      }}
                      data-testid={`subscription_radio-${sellingPlans.indexOf(planEdge) + 1}`}
                      className={`w-full text-left transition-all bg-white p-4 rounded-[6px] border ${
                        isSelected
                          ? "border-black"
                          : selectionError
                            ? "border-[#E34D52]"
                            : "border-[#E0E0E0]"
                      }`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="text-black font-normal text-base leading-6" style={{ fontFamily: 'TT Commons Pro' }}>
                          {plan.name}
                        </div>
                        {isRecommended && subscriptionT.mostPopular && (
                          <span
                            className="flex-shrink-0 bg-[#3CC34E1F] rounded-[20px] px-2.5 font-semibold text-xs leading-5 text-center"
                            style={{ fontFamily: 'TT Commons Pro' }}
                          >
                            {subscriptionT.mostPopular}
                          </span>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Pricing Summary */}
            <div className="rounded-[6px] overflow-hidden mb-3">
              {/* Top part - Price breakdown and Consultation Fee */}
              <div className="bg-[#F7F1F1] p-4">
                {/* Price breakdown */}
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <span className="font-normal text-base leading-6" style={{ fontFamily: 'TT Commons Pro' }}>
                      {selectedQuantity}
                    </span>
                    {/* Save badge from variant metafield */}
                    {variantQuantityBadge && (
                      <span
                        className="bg-[#3CC34E1F] rounded-[20px] px-2.5 font-semibold text-xs leading-5 text-center"
                        style={{ fontFamily: 'TT Commons Pro' }}
                      >
                        {variantQuantityBadge}
                      </span>
                    )}
                  </div>
                  <div>
                    {/* Show original price with strikethrough if there's a discount */}
                    {savingsPercentage > 0 && (
                      <span className="line-through text-gray-500 mr-2 font-normal text-sm" style={{ fontFamily: 'TT Commons Pro' }}>
                        ${variantPrice.toFixed(2)}
                      </span>
                    )}
                    <span className="text-black font-semibold text-base leading-6 text-right" style={{ fontFamily: 'TT Commons Pro' }}>
                      ${discountedPriceWithShipping.toFixed(2)}
                    </span>
                  </div>
                </div>

                {/* Consultation Fee - Only show if consultation product is configured */}
                {consultationFee && consultationProductTitle && (
                  <div className="flex items-start justify-between">
                    <div className="flex flex-col">
                      <span className="text-black mb-1 font-normal text-base leading-6" style={{ fontFamily: 'TT Commons Pro' }}>
                        {consultationProductTitle}
                      </span>
                      <button
                        type="button"
                        data-consultation-modal-trigger
                        aria-label={`${subscriptionT.whatIsThis} - ${consultationProductTitle}`}
                        data-testid="subscription-what_is_this"
                        className="underline text-black text-left font-semibold text-[15px] leading-[21px]"
                        style={{ fontFamily: 'TT Commons Pro' }}
                      >
                        {subscriptionT.whatIsThis}
                      </button>
                    </div>
                    <span className="text-black font-semibold text-base leading-6 text-right" style={{ fontFamily: 'TT Commons Pro' }}>
                      ${consultationFeeWithSurcharge.toFixed(2)}
                    </span>
                  </div>
                )}
              </div>

              {/* Bottom part - Due Today */}
              <div className="bg-white p-4">
                <div className="flex items-center justify-between">
                  <span className="text-black font-semibold text-base leading-6" style={{ fontFamily: 'TT Commons Pro' }}>
                    {subscriptionT.dueToday}
                  </span>
                  <span className="text-black font-semibold text-base leading-6 text-right" style={{ fontFamily: 'TT Commons Pro' }}>
                    ${dueToday.toFixed(2)}
                  </span>
                </div>
              </div>
            </div>

            {/* Recurring Price */}
            <div>
              <div className="flex items-center justify-between p-4 mb-3">
                <div className="flex items-center gap-2">
                  <svg width="15" height="14" viewBox="0 0 15 14" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
                    <path d="M2.4375 4.875C2.25 5.25 1.8125 5.40625 1.4375 5.25C1.0625 5.09375 0.875 4.65625 1.03125 4.28125C2.09375 1.78125 4.59375 0 7.5 0C9.125 0 10.6875 0.65625 11.8438 1.8125L12 1.96875L13.0938 0.84375C13.3125 0.625 13.5938 0.5 13.875 0.5C14.5 0.5 15 1 15 1.625V5.25C15 5.6875 14.6562 6 14.25 6H10.5938C9.96875 6 9.5 5.53125 9.5 4.90625C9.5 4.625 9.59375 4.34375 9.8125 4.125L10.9375 3L10.7812 2.875C9.90625 2 8.71875 1.5 7.5 1.5C5.21875 1.5 3.25 2.90625 2.4375 4.875ZM13.9375 9.78125C12.8438 12.25 10.375 14 7.5 14C5.84375 14 4.28125 13.375 3.125 12.1875L3 12.0625L1.875 13.1875C1.65625 13.4062 1.375 13.5 1.09375 13.5C0.46875 13.5 0 13.0312 0 12.4062V8.75C0 8.34375 0.3125 8 0.75 8H4.375C5 8 5.5 8.5 5.5 9.125C5.5 9.40625 5.375 9.6875 5.15625 9.90625L4.0625 11L4.1875 11.1562C5.0625 12.0312 6.25 12.5 7.5 12.5C9.75 12.5 11.7188 11.1562 12.5312 9.1875C12.7188 8.78125 13.1562 8.625 13.5312 8.78125C13.9062 8.9375 14.0938 9.375 13.9375 9.78125ZM13.5 4.5V2.5625L11.5312 4.5H13.5ZM1.5 9.5V11.4688L3.4375 9.5H1.5Z" fill="black"/>
                  </svg>
                  <span className="text-black font-normal text-base leading-6" style={{ fontFamily: 'TT Commons Pro' }}>
                    {subscriptionT.recurringPrice}
                  </span>
                </div>
                <div className="text-right">
                  <div className="text-black font-semibold text-sm leading-5" style={{ fontFamily: 'TT Commons Pro' }}>
                    ${recurringPrice.toFixed(2)}
                  </div>
                  <div className="text-black font-medium text-xs leading-[18px]" style={{ fontFamily: 'TT Commons Pro' }}>
                    ${perDosePrice.toFixed(2)}/{subscriptionT.dose}
                  </div>
                </div>
              </div>

              {/* Refund Policy */}
              <div className="px-4">
                <p className="text-black font-normal text-sm leading-[21px]" style={{ fontFamily: 'TT Commons Pro' }}>
                  {subscriptionT.refundPolicy}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Continue Button — always full colour; validate on submit */}
        <div className="px-4 sm:px-6">
          <div className="max-w-[480px] mx-auto sm:max-w-[640px] pb-[30px]">
            {selectionError && (
              <p className="text-[#E34D52] text-sm mb-3" role="alert" style={{ fontFamily: "TT Commons Pro" }}>
                {selectionError}
              </p>
            )}
            <Button
              type="button"
              onClick={handleContinue}
              data-testid="subscription-continue"
              className="w-full h-[50px] text-white text-lg leading-6 font-medium rounded-full flex items-center justify-center transition-colors bg-[#2D55FB] hover:bg-[#2347E8]"
            >
              {subscriptionT.continue}
            </Button>

            {/* Tax Notice */}
            <p className="text-center mt-3 text-gray-600 font-normal text-sm leading-5" style={{ fontFamily: 'TT Commons Pro' }}>
              {subscriptionT.taxNotice}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
